import { EditMemberOnboardingSearchParams } from "@/admin/member-onboarding/EditMemberOnboardingContext"
import {
  LegacyCreateSetupAppModalFormStore,
  LegacyEditSetupAppModalFormStore,
} from "@/apps/list/modal/SetupAppModal"
import { useAppLevel } from "@/apps/util/appLevelContext"
import { ProductAppVisibility } from "@/apps/util/hooks/__generated__/useAddAppMutation.graphql"
import { useLabels } from "@/core/context/LabelsContext"
import ROUTE_NAMES from "@/core/route/util/routeNames"
import ProductUtils from "@/product/util/productUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import {
  DiscoAlert,
  DiscoButton,
  DiscoFormControl,
  DiscoIcon,
  DiscoSelect,
  DiscoText,
} from "@disco-ui"
import { TestIDProps } from "@utils/typeUtils"
import { setSearchParams } from "@utils/url/urlUtils"
import { observer } from "mobx-react-lite"

interface AppVisibilityFormProps extends TestIDProps {
  form: LegacyCreateSetupAppModalFormStore | LegacyEditSetupAppModalFormStore
  alert?: React.ReactNode
  disabled?: boolean
}

function AppVisibilityForm({
  testid = "AppVisibilityForm",
  form,
  alert,
  disabled = false,
}: AppVisibilityFormProps) {
  const { product } = useAppLevel()
  const labels = useLabels()
  const memberLabel = product
    ? labels.product_member.plural
    : labels.organization_member.plural
  const classes = useStyles()

  // Don't allow changing visibility if onboarding completion redirects to this app
  const isOnboardingRedirect =
    "isOnboardingRedirect" in form.state &&
    form.state.isOnboardingRedirect &&
    form.state.visibility === "all"

  return (
    <DiscoFormControl
      label={<DiscoText variant={"body-sm"}>{"Who can access?"}</DiscoText>}
      errorMessages={form.errorsByField.visibility}
    >
      {renderAlert()}
      <DiscoSelect
        value={form.state.visibility}
        testid={`${testid}.access-select`}
        disableClearable
        disabled={disabled || isOnboardingRedirect}
        options={[
          {
            value: "all",
            title: `All ${memberLabel}`,
            subtitle: `All ${memberLabel} of the ${
              product ? labels.admin_experience.singular : labels.organization.singular
            } can access.`,
            context: {
              icon: ProductUtils.getAvailabilityIcon("public"),
            },
          },
          {
            value: "membership",
            title: "Private",
            subtitle: `Only specific ${memberLabel} or groups can access.`,
            context: {
              icon: ProductUtils.getAvailabilityIcon("private"),
            },
          },
        ]}
        renderOption={(option) => (
          <div className={classes.accessOption}>
            <DiscoIcon icon={option.context.icon} color={"currentColor"} />
            <div>
              <DiscoText
                testid={`${testid}.access-select-option`}
                variant={"body-md-500"}
              >
                {option.title}
              </DiscoText>
              <DiscoText>{option.subtitle}</DiscoText>
            </div>
          </div>
        )}
        onChange={(v) => {
          form.state.visibility = v as ProductAppVisibility
        }}
      />
    </DiscoFormControl>
  )

  function renderAlert() {
    if (alert) return alert
    if (isOnboardingRedirect)
      return (
        <DiscoAlert
          marginBottom={1.5}
          severity={"warning"}
          title={`This app is where ${memberLabel} land after onboarding.`}
          message={`To change this app's access, edit onboarding settings and choose a different app for ${memberLabel} to land on after they complete onboarding.`}
          cta={
            <DiscoButton
              to={{
                pathname: ROUTE_NAMES.ADMIN.MEMBERS.ONBOARDING.ROOT,
                search: setSearchParams<EditMemberOnboardingSearchParams>("", {
                  openKind: "onboarding_complete",
                }),
              }}
            >
              {"Edit Onboarding"}
            </DiscoButton>
          }
        />
      )
    return null
  }
}

const useStyles = makeUseStyles((theme) => ({
  accessOption: {
    display: "flex",
    gap: theme.spacing(1),
  },
}))

export default observer(AppVisibilityForm)
